<script setup lang="ts">
import type { Spaces } from '../../lib/types.ts'
import { useEventListener, useScroll } from '@vueuse/core'
import { computed, onMounted, ref, useTemplateRef } from 'vue'
import Flex from '../Flex/Flex.vue'
import '../Overflow/overflow.scss'
import './carousel.scss'

const {
  gap = 's' as const,
  hideScrollbar,
  hideShadows,
  snap,
  disableDrag,
} = defineProps<Props>()

/**
 * When using the carousel, the items put within should generally have a `min-width` set.
 * Otherwise the flex will try to fit all items without overflowing.
 *
 * When using Carousel, you should set `:draggable="false"` to avoid issues with dragging.
 */
interface Props {
  /**
   * We use flex to gap items.
   */
  gap?: Spaces | number
  /**
   * Hides the horizontal scrollbar and removes scrollbar gutter padding
   */
  hideScrollbar?: boolean
  /**
   * Hides the left & right shadows which indicate option to scroll
   */
  hideShadows?: boolean
  /**
   * Enables scroll-snap so items snap into place when scrolling stops
   */
  snap?: boolean
  /**
   * Disables the drag-to-scroll functionality. Keeping only mouse wheel and
   * touch scrolling. Useful when the carousel contains interactive elements
   * like buttons or links that would conflict with dragging.
   */
  disableDrag?: boolean
}

const carouselRef = useTemplateRef<InstanceType<typeof Flex>>('carouselWrap')
const carouselEl = computed(() => carouselRef.value?.$el as HTMLElement | undefined)

const { x } = useScroll(carouselEl)

// Drag-to-scroll state
const isDragging = ref(false)
const hasDragged = ref(false)
const dragStartX = ref(0)
const scrollStartX = ref(0)

// How many pixels the pointer must move before it's considered a drag and not a click
const DRAG_THRESHOLD = 6

useEventListener(carouselEl, 'pointerdown', (e: PointerEvent) => {
  if (disableDrag)
    return

  // Only handle left mouse button (or touch/pen)
  if (e.button !== 0 && e.pointerType === 'mouse')
    return

  const el = carouselEl.value
  if (!el)
    return

  // Reset any stale drag state from a previous interaction where click never fired
  hasDragged.value = false
  isDragging.value = true
  dragStartX.value = e.clientX
  // Read scrollLeft directly - more reliable than the reactive x ref
  scrollStartX.value = el.scrollLeft
  // Note: do NOT call setPointerCapture here - that would swallow the click
  // event for plain taps. We capture only once a real drag is confirmed.
})

useEventListener(carouselEl, 'pointermove', (e: PointerEvent) => {
  if (disableDrag || !isDragging.value)
    return

  const delta = dragStartX.value - e.clientX

  // Only start scrolling once we've exceeded the threshold
  if (!hasDragged.value && Math.abs(delta) < DRAG_THRESHOLD)
    return

  // First frame past the threshold: capture the pointer so we keep receiving
  // events even when the cursor leaves the element, then start scrolling.
  if (!hasDragged.value) {
    const el = carouselEl.value
    el?.setPointerCapture(e.pointerId)
  }

  hasDragged.value = true
  // Write directly to scrollLeft for immediate, lag-free response
  const el = carouselEl.value
  if (el)
    el.scrollLeft = scrollStartX.value + delta
})

useEventListener(carouselEl, 'pointerup', () => {
  isDragging.value = false
})

useEventListener(carouselEl, 'pointercancel', () => {
  isDragging.value = false
  hasDragged.value = false
})

// Suppress the click that fires immediately after a drag so child links/buttons don't activate
useEventListener(carouselEl, 'click', (e: MouseEvent) => {
  if (hasDragged.value) {
    e.preventDefault()
    e.stopPropagation()
    hasDragged.value = false
  }
}, { capture: true })

// Wheel listener must be attached in onMounted so we can pass the resolved DOM
// element directly - guaranteeing { passive: false } is respected, which is
// required for e.preventDefault() to suppress vertical page scroll.
onMounted(() => {
  useEventListener(carouselEl.value, 'wheel', (e: WheelEvent) => {
    // If already scrolling horizontally (shift+wheel or trackpad swipe), let it pass
    if (e.deltaX !== 0)
      return

    e.preventDefault()
    carouselEl.value!.scrollLeft += e.deltaY
  }, { passive: false })
})
</script>

<template>
  <div
    class="vui-overflow is-horizontal carousel"
    :class="{
      'hide-scrollbar': hideScrollbar,
      'hide-shadows': hideShadows,
      'no-drag': disableDrag,
      'is-snap': snap,
    }"
  >
    <div class="overflow-track">
      <div class="overflow-shadow overflow-shadow-left" :class="{ visible: x > 0 }" />
      <div
        class="overflow-shadow overflow-shadow-right"
        :class="{ visible: carouselEl && x < carouselEl.scrollWidth - carouselEl.clientWidth }"
      />
      <Flex
        ref="carouselWrap"
        class="overflow-content"
        :class="{ 'is-dragging': isDragging }"
        :gap
      >
        <slot />
      </Flex>
    </div>
  </div>
</template>
